import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { ChevronIcon } from '../components/icons';
import { FeedGrid } from '../components/feed-grid';
import { useTagFeedArticles, useTagFeedQuery } from '../hooks/use-feed-query';
import {
  useSubscribeTopicMutation,
  useTopicSubscriptionsQuery,
  useUnsubscribeTopicMutation,
} from '../hooks/use-topics-query';

export function TagFeed() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tag: rawTag } = useParams<{ tag: string }>();
  const tag = (rawTag ?? '').toLowerCase();

  const feedQuery = useTagFeedQuery(tag);
  const articles = useTagFeedArticles(tag);
  const subs = useTopicSubscriptionsQuery();
  const subscribe = useSubscribeTopicMutation();
  const unsubscribe = useUnsubscribeTopicMutation();

  const isFollowing = (subs.data ?? []).some((s) => s.topicSlug === tag);

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.tagFeed.sidebar.label')}</div>
        <button type="button" className="nw-side__item" onClick={() => navigate('/home')}>
          <ChevronIcon dir="left" size={16} />
          <span className="nw-side__item-label">{t('web.tagFeed.sidebar.backToFeed')}</span>
        </button>
        <div className="nw-side__divider" />
        <div className="nw-side__label">{t('web.tagFeed.sidebar.topicLabel')}</div>
        <div style={{ padding: '6px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
          <Trans
            i18nKey="web.tagFeed.sidebar.showingTagged"
            values={{ tag }}
            components={{ strong: <strong style={{ color: 'var(--accent)' }} /> }}
          />
        </div>
      </aside>
      <section className="nw-feed">
        <div className="nw-tag-header">
          <div className="nw-tag-header__hash">#</div>
          <div className="nw-tag-header__body">
            <h1 className="nw-tag-header__title">{tag}</h1>
            <p className="nw-tag-header__sub">
              <Trans
                i18nKey="web.tagFeed.subtitle"
                values={{ tag }}
                components={{ code: <code /> }}
              />
            </p>
          </div>
          <button
            type="button"
            className={`nw-channel-row__follow ${isFollowing ? 'is-following' : ''}`}
            disabled={subscribe.isPending || unsubscribe.isPending}
            onClick={() => {
              if (isFollowing) unsubscribe.mutate(tag);
              else subscribe.mutate(tag);
            }}
          >
            {isFollowing ? t('web.tagFeed.following') : t('web.tagFeed.follow')}
          </button>
        </div>
        <FeedGrid
          articles={articles}
          isLoading={feedQuery.isLoading}
          isError={feedQuery.isError}
          emptyMessage={t('web.tagFeed.empty', { tag })}
          hasNextPage={feedQuery.hasNextPage}
          fetchNextPage={() => feedQuery.fetchNextPage()}
          isFetchingNextPage={feedQuery.isFetchingNextPage}
        />
      </section>
    </div>
  );
}
